/**
 * Sensor-Fusion für ein sauberes Fahrgefühl:
 *
 *  - GPS (geolocation): liefert die echte Fahrzeuggeschwindigkeit. Driftfrei,
 *    aber träge (~1 s Updates). → Grundwahrheit für die Drehzahl.
 *  - DeviceMotion (G-Sensor): liefert die Längs-/Querbeschleunigung in m/s².
 *    Schnell, aber driftet beim Integrieren. → schnelle Reaktion + Klangcharakter.
 *
 * Die Physik integriert die Beschleunigung für sofortige Reaktion und korrigiert
 * die Geschwindigkeit kontinuierlich gegen das GPS (siehe physics.js). Dadurch
 * folgt der Klang sauber der Geschwindigkeit, ohne bei konstanter Fahrt zu springen.
 */
import { state } from '../state/store.js'
import { clamp, smooth, log } from '../util.js'
import { eventBoom } from '../audio/events.js'

const G = 9.81
const CALIBRATION_SAMPLES = 24
const EARTH_RADIUS_M = 6371000

function degToRad(value) {
  return (value * Math.PI) / 180
}

function distanceMeters(a, b) {
  const dLat = degToRad(b.latitude - a.latitude)
  const dLon = degToRad(b.longitude - a.longitude)
  const lat1 = degToRad(a.latitude)
  const lat2 = degToRad(b.latitude)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

export function createCalibrationState(axis = 'x') {
  return {
    status: 'sampling',
    axis,
    count: 0,
    sumX: 0,
    sumY: 0,
    sumZ: 0,
    longBias: 0,
    latBias: 0,
  }
}

export function sampleCalibration(calibration, raw) {
  const next = calibration || createCalibrationState()
  if (next.status === 'ready') return next

  const count = next.count + 1
  const sumX = next.sumX + (raw.x || 0)
  const sumY = next.sumY + (raw.y || 0)
  const sumZ = next.sumZ + (raw.z || 0)
  const ready = count >= CALIBRATION_SAMPLES
  const avgX = sumX / count
  const avgY = sumY / count

  return {
    ...next,
    status: ready ? 'ready' : 'sampling',
    count,
    sumX,
    sumY,
    sumZ,
    longBias: ready ? (next.axis === 'y' ? avgY : avgX) : next.longBias,
    latBias: ready ? (next.axis === 'y' ? avgX : avgY) : next.latBias,
  }
}

export function resolveMotionAxes(raw, calibration = createCalibrationState()) {
  const axis = calibration.axis === 'y' ? 'y' : 'x'
  const longRaw = axis === 'y' ? raw.y || 0 : raw.x || 0
  const latRaw = axis === 'y' ? raw.x || 0 : raw.y || 0
  const ready = calibration.status === 'ready'

  return {
    axis,
    longRaw,
    latRaw,
    longCorrected: longRaw - (ready ? calibration.longBias || 0 : 0),
    latCorrected: latRaw - (ready ? calibration.latBias || 0 : 0),
  }
}

function preferredAxis() {
  try {
    return window.innerHeight > window.innerWidth ? 'y' : 'x'
  } catch {
    return 'x'
  }
}

async function requestMotionAccess() {
  state.motionPermission = 'unavailable'
  state.sensorPermissionIssue = ''

  if (typeof DeviceMotionEvent === 'undefined') {
    state.sensorPermissionIssue = 'motion-unavailable'
    return false
  }

  if (typeof DeviceMotionEvent.requestPermission === 'function') {
    try {
      state.motionPermission = 'prompt'
      const permission = await DeviceMotionEvent.requestPermission()
      state.motionPermission = permission
      if (permission !== 'granted') {
        state.sensorPermissionIssue = 'motion-denied'
        return false
      }
    } catch (e) {
      state.motionPermission = 'denied'
      state.sensorPermissionIssue = e?.message || 'motion-denied'
      return false
    }
  } else {
    state.motionPermission = 'granted'
  }

  return true
}

export function applyGeoPosition(pos) {
  const coords = pos.coords || {}
  const timestamp = pos.timestamp || Date.now()
  const latitude = coords.latitude
  const longitude = coords.longitude
  const accuracy = coords.accuracy || 0

  state.gpsPermission = 'granted'
  state.gpsActive = true
  state.gpsAccuracy = accuracy
  state.gpsAgeSec = 0

  const directSpeed = coords.speed
  if (directSpeed != null && !Number.isNaN(directSpeed) && directSpeed >= 0) {
    state.gpsSpeed = directSpeed * 3.6
    state.gpsSpeedSource = 'coords-speed'
  } else if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    const current = { latitude, longitude, timestamp, accuracy }
    const previous = state.gpsLastPosition
    if (previous?.timestamp && timestamp > previous.timestamp) {
      const seconds = (timestamp - previous.timestamp) / 1000
      const meters = distanceMeters(previous, current)
      if (seconds >= 0.5 && meters >= Math.max(2, accuracy * 0.2)) {
        state.gpsSpeed = clamp((meters / seconds) * 3.6, 0, 280)
        state.gpsSpeedSource = 'position-delta'
      }
    }
    state.gpsLastPosition = current
  }
}

/** DeviceMotion-Handler: leitet geglättete Längs-/Querbeschleunigung ab. */
function onMotion(e) {
  try {
    const a = e.accelerationIncludingGravity || e.acceleration || {}
    const ax = a.x || 0,
      ay = a.y || 0,
      az = a.z || 0

    if (!state.sensorCalibration || state.sensorCalibration.status !== 'ready') {
      state.sensorCalibration = sampleCalibration(
        state.sensorCalibration || createCalibrationState(state.sensorAxis || preferredAxis()),
        { x: ax, y: ay, z: az },
      )
      state.sensorAxis = state.sensorCalibration.axis
    }

    const axes = resolveMotionAxes({ x: ax, y: ay, z: az }, state.sensorCalibration)
    state.longRaw = axes.longRaw
    state.latRaw = axes.latRaw

    // Ruhelage (inkl. Schwerkraftanteil) als Nullpunkt abziehen → reine Fahrdynamik.
    const longC = axes.longCorrected
    const latC = axes.latCorrected
    state.longBias = state.sensorCalibration?.longBias || 0
    state.latBias = state.sensorCalibration?.latBias || 0

    const prev = state.longAccelMs || 0
    // Starke Glättung gegen Sensorrauschen (Tiefpass).
    state.longAccelMs = smooth(state.longAccelMs, longC, 0.12)
    state.latAccelMs = smooth(state.latAccelMs, latC, 0.12)

    // Abgeleitete 0..1-Anzeigewerte für das HUD.
    state.sensorAccel = smooth(state.sensorAccel, clamp(state.longAccelMs / 4, 0, 1), 0.2)
    state.sensorBrake = smooth(state.sensorBrake, clamp(-state.longAccelMs / 5, 0, 1), 0.2)
    state.gSensor = state.longAccelMs / G
    state.lateralG = state.latAccelMs / G
    state.sensorConfidence = smooth(
      state.sensorConfidence,
      clamp((Math.abs(ax) + Math.abs(ay) + Math.abs(az) - 7.8) / 7, 0, 1),
      0.08,
    )

    // Echter Ruck (m/s² pro Frame) → einmaliger G-Punch, mit Cooldown gegen Spam.
    const jerk = state.longAccelMs - prev
    state.sensorJerk = smooth(state.sensorJerk, Math.max(0, jerk), 0.25)
    if (jerk > 1.8 && performance.now() - (state._lastGPunch || 0) > 700) {
      state._lastGPunch = performance.now()
      state.gPunch = 1
      eventBoom('gPunch')
    }
  } catch {}
}

/* ----------------------------------------------------------------- GPS */

let geoWatchId = null

function startGeo() {
  if (!('geolocation' in navigator)) {
    state.gpsPermission = 'unavailable'
    log.warn('GPS nicht verfügbar')
    return false
  }
  try {
    state.gpsPermission = 'prompt'
    if (typeof navigator.geolocation.getCurrentPosition === 'function') {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          applyGeoPosition(pos)
        },
        (err) => {
          state.gpsActive = false
          state.gpsPermission = 'denied'
          state.sensorPermissionIssue = state.sensorPermissionIssue || err.message
          log.warn('GPS:', err.message)
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 12000 },
      )
    }
    geoWatchId = navigator.geolocation.watchPosition(
      (pos) => {
        applyGeoPosition(pos)
      },
      (err) => {
        state.gpsActive = false
        state.gpsPermission = 'denied'
        state.sensorPermissionIssue = state.sensorPermissionIssue || err.message
        log.warn('GPS:', err.message)
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 12000 },
    )
    return true
  } catch (e) {
    state.gpsPermission = 'denied'
    state.sensorPermissionIssue = state.sensorPermissionIssue || e.message
    log.warn('GPS:', e.message)
    return false
  }
}

function stopGeo() {
  if (geoWatchId != null) {
    try {
      navigator.geolocation.clearWatch(geoWatchId)
    } catch {}
    geoWatchId = null
  }
  state.gpsActive = false
  state.gpsSpeed = 0
  state.gpsLastPosition = null
  state.gpsAccuracy = 0
  state.gpsSpeedSource = 'none'
  state.gpsAgeSec = 0
  state.gpsLeadKmh = 0
  state.gpsPermission = 'unknown'
}

/* ------------------------------------------------------------- Steuerung */

/**
 * Aktiviert/deaktiviert die Fahrsensorik (DeviceMotion + GPS).
 * Fordert auf iOS die Bewegungsberechtigung an.
 * @returns {Promise<boolean>} neuer Zustand
 */
export async function toggleSensor() {
  if (!state.sensor) {
    const motionGranted = await requestMotionAccess()
    state.sensor = true
    state.sensorAxis = preferredAxis()
    state.sensorCalibration = createCalibrationState(state.sensorAxis)
    state.longAccelMs = 0
    state.latAccelMs = 0
    state.sensorAccel = 0
    state.sensorBrake = 0
    if (motionGranted) window.addEventListener('devicemotion', onMotion)
    startGeo()
  } else {
    state.sensor = false
    window.removeEventListener('devicemotion', onMotion)
    stopGeo()
    state.longAccelMs = 0
    state.latAccelMs = 0
    state.gSensor = 0
    state.lateralG = 0
    state.sensorAccel = 0
    state.sensorBrake = 0
    state.sensorCalibration = null
    state.motionPermission = 'unknown'
    state.sensorPermissionIssue = ''
  }
  return state.sensor
}

/** Sensorik erzwungen deaktivieren. */
export function stopSensor() {
  window.removeEventListener('devicemotion', onMotion)
  stopGeo()
  state.sensor = false
}
